import { useId, type ReactNode } from "react";
import { Platform } from "@-/com.npmjs.react-native";
import {
  createAnimatedComponent,
  useAnimatedProps,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import Svg, { ClipPath, Defs, G, Path } from "@-/com.npmjs.react-native-svg";
import type {
  GraphemeGlyphCanvasProps,
  GraphemeGlyphRenderStroke,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg.entities";
import {
  getGraphemeSvgGlyphStrokeDashoffset,
  getGraphemeSvgGlyphStrokeLocalProgress,
  GRAPHEME_SVG_GLYPH_CELL_UNITS,
  GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH,
} from "#grapheme-svg-glyph-animation";
import { GraphemeSvgGlyphStripGuide } from "#grapheme-svg-glyph-strip-guide";

const AnimatedPath = createAnimatedComponent(Path);
const AnimatedG = createAnimatedComponent(G);
const IS_WEB = Platform.OS === "web";

function getClipPathId(instanceId: string, glyphKey: string, index: number): string {
  // Code points keep the fragment identifier ASCII while still changing with the drawn text.
  const codePoints = Array.from(glyphKey, (character) => character.codePointAt(0)).join("-");
  return `player-stroke-clip-${instanceId}-${codePoints}-${index}`;
}

function getStrokeDrawState(
  progressValue: number,
  index: number,
  stroke: GraphemeGlyphRenderStroke,
  totalStrokes: number,
) {
  const localProgress = getGraphemeSvgGlyphStrokeLocalProgress(progressValue, index, totalStrokes);
  return {
    medianOpacity: localProgress > 0 ? 1 : 0,
    medianStrokeDashoffset: getGraphemeSvgGlyphStrokeDashoffset(stroke.length, localProgress),
  };
}

/** Legacy SVG presentation, also used by the browser and while native GPU resources load. */
// eslint-disable-next-line filename-export/match-named-export -- Platform renderers share the canonical canvas export.
export function GraphemeGlyphCanvas({
  columnCount,
  glyphKey,
  guidePalette,
  highlightedStrokeFill,
  highlightedStrokeIndex,
  progress,
  progressValue,
  showBackground,
  size,
  strokeFill,
  strokeRenderList,
  upcomingFill,
}: GraphemeGlyphCanvasProps) {
  // Keep fragment identifiers valid in both native SVG and browser URL resolvers.
  const instanceId = useId().replaceAll(":", "");
  const totalStrokes = strokeRenderList.length;
  const webProgress = progressValue ?? 0;
  const isStatic = IS_WEB || progressValue !== undefined;

  return (
    <Svg
      height={size}
      viewBox={`0 0 ${GRAPHEME_SVG_GLYPH_CELL_UNITS * columnCount} ${GRAPHEME_SVG_GLYPH_CELL_UNITS}`}
      width={size * columnCount}
    >
      <Defs>
        {strokeRenderList.map((stroke, index) => (
          <ClipPath id={getClipPathId(instanceId, glyphKey, index)} key={index}>
            <Path d={stroke.outlinePath} />
          </ClipPath>
        ))}
      </Defs>
      {showBackground && (
        <GraphemeSvgGlyphStripGuide columnCount={columnCount} guidePalette={guidePalette} />
      )}
      <G transform="scale(1,-1) translate(0,-900)">
        {/*
          Ghost layer. Drawn exactly like the animated layer (clipped median, fully
          revealed) rather than as the filled outline, so a finished stroke covers its ghost
          pixel-for-pixel — a filled outline can leave grey slivers wherever the 200-unit
          median brush doesn't quite reach the outline's edge. This is what Dong Chinese does.
        */}
        <G>
          {strokeRenderList.map((stroke, index) => (
            <Path
              clipPath={`url(#${getClipPathId(instanceId, glyphKey, index)})`}
              d={stroke.medianPathData}
              fill="none"
              key={index}
              stroke={upcomingFill}
              strokeDasharray={`${stroke.length},${stroke.length}`}
              strokeDashoffset={0}
              strokeLinecap="round"
              strokeLinejoin="miter"
              strokeWidth={GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH}
            />
          ))}
        </G>
        <GraphemeSvgGlyphForeground
          progress={progress}
          progressValue={isStatic ? webProgress : undefined}
        >
          {strokeRenderList.map((stroke, index) => (
            <GraphemeSvgGlyphCanvasStroke
              glyphKey={glyphKey}
              index={index}
              instanceId={instanceId}
              key={index}
              progress={progress}
              progressValue={isStatic ? webProgress : undefined}
              stroke={stroke}
              strokeFill={
                index === highlightedStrokeIndex
                  ? (highlightedStrokeFill ?? strokeFill)
                  : strokeFill
              }
              totalStrokes={totalStrokes}
            />
          ))}
        </GraphemeSvgGlyphForeground>
      </G>
    </Svg>
  );
}

/** Fixed preload frames retain every native path without installing animation mappers. */
function GraphemeSvgGlyphForeground({
  children,
  progress,
  progressValue,
}: {
  children: ReactNode;
  progress: SharedValue<number>;
  progressValue?: number;
}) {
  if (progressValue !== undefined) {
    return <G opacity={progressValue <= 1 ? 1 : Math.max(0, 2 - progressValue)}>{children}</G>;
  }
  return (
    <GraphemeSvgGlyphAnimatedForeground progress={progress}>
      {children}
    </GraphemeSvgGlyphAnimatedForeground>
  );
}

function GraphemeSvgGlyphAnimatedForeground({
  children,
  progress,
}: {
  children: ReactNode;
  progress: SharedValue<number>;
}) {
  const animatedProps = useAnimatedProps<{ opacity: number }>(() => ({
    opacity: progress.get() <= 1 ? 1 : Math.max(0, 2 - progress.get()),
  }));
  return <AnimatedG animatedProps={animatedProps}>{children}</AnimatedG>;
}

type GraphemeSvgGlyphCanvasStrokeProps = {
  glyphKey: string;
  index: number;
  instanceId: string;
  progress: SharedValue<number>;
  /** A fixed frame has no animated hooks or subscriptions. */
  progressValue?: number;
  stroke: GraphemeGlyphRenderStroke;
  strokeFill: string;
  totalStrokes: number;
};

function GraphemeSvgGlyphCanvasStroke(props: GraphemeSvgGlyphCanvasStrokeProps) {
  const { glyphKey, index, instanceId, progressValue, stroke, strokeFill, totalStrokes } = props;
  if (progressValue === undefined) {
    return <GraphemeSvgGlyphAnimatedStroke {...props} />;
  }
  const { medianOpacity, medianStrokeDashoffset } = getStrokeDrawState(
    progressValue,
    index,
    stroke,
    totalStrokes,
  );
  return (
    <Path
      clipPath={`url(#${getClipPathId(instanceId, glyphKey, index)})`}
      d={stroke.medianPathData}
      fill="none"
      opacity={medianOpacity}
      stroke={strokeFill}
      strokeDasharray={`${stroke.length},${stroke.length}`}
      strokeDashoffset={medianStrokeDashoffset}
      strokeLinecap="round"
      strokeLinejoin="miter"
      strokeWidth={GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH}
    />
  );
}

function GraphemeSvgGlyphAnimatedStroke({
  glyphKey,
  index,
  instanceId,
  progress,
  stroke,
  strokeFill,
  totalStrokes,
}: GraphemeSvgGlyphCanvasStrokeProps) {
  const activeMedianAnimatedProps = useAnimatedProps<{ opacity: number; strokeDashoffset: number }>(
    () => {
      const localProgress = getGraphemeSvgGlyphStrokeLocalProgress(
        progress.get(),
        index,
        totalStrokes,
      );
      return {
        opacity: localProgress > 0 ? 1 : 0,
        strokeDashoffset: getGraphemeSvgGlyphStrokeDashoffset(stroke.length, localProgress),
      };
    },
  );
  return (
    <AnimatedPath
      animatedProps={activeMedianAnimatedProps}
      clipPath={`url(#${getClipPathId(instanceId, glyphKey, index)})`}
      d={stroke.medianPathData}
      fill="none"
      stroke={strokeFill}
      strokeDasharray={`${stroke.length},${stroke.length}`}
      strokeLinecap="round"
      strokeLinejoin="miter"
      strokeWidth={GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH}
    />
  );
}
